import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function UsersPage() {
  const { isAdmin, user } = useAuth();
  const canCreateUsers = isAdmin || user?.role === 'client';
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const emptyForm = {
    name: '',
    email: '',
    password: '',
    role: 'client',
    projectIds: '',
    vendor_user_id: '',
    phone: '',
    organization: '',
    organization_address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [projectDetails, setProjectDetails] = useState(null);
  const [projectDetailsLoading, setProjectDetailsLoading] = useState(false);
  const [gpuCapacityModalOpen, setGpuCapacityModalOpen] = useState(false);
  const [gpuCapacityOwner, setGpuCapacityOwner] = useState(null);
  const [gpuCapacityRows, setGpuCapacityRows] = useState([]);
  const [gpuCapacityLoading, setGpuCapacityLoading] = useState(false);
  const [gpuCapacityError, setGpuCapacityError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users');
      setUsers(data.users || []);
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async () => {
    setSaving(true); setError('');
    try {
      const projectIds = form.projectIds.split(',').map(p => p.trim()).filter(Boolean);
      const payload = { ...form, role: isAdmin ? form.role : 'client_user', projectIds };
      if (!payload.password) delete payload.password;
      const { data } = await api.post('/users', payload);
      setSuccess(data.temporaryPassword ? `User created. Temporary password: ${data.temporaryPassword}` : 'User created successfully');
      setShowModal(false);
      setForm(emptyForm);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id, is_active) => {
    try {
      await api.put(`/users/${id}`, { is_active: !is_active });
      fetchUsers();
    } catch { setError('Failed to update user'); }
  };

  const openCreate = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (selectedUser) => {
    setEditingUser(selectedUser);
    setForm({
      ...emptyForm,
      name: selectedUser.name || '',
      email: selectedUser.email || '',
      role: selectedUser.role || 'client',
      projectIds: selectedUser.assigned_projects || '',
      vendor_user_id: selectedUser.vendor_user_id || '',
      phone: selectedUser.phone || '',
      organization: selectedUser.organization || '',
      organization_address: selectedUser.organization_address || '',
      city: selectedUser.city || '',
      state: selectedUser.state || '',
      country: selectedUser.country || '',
      postal_code: selectedUser.postal_code || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!editingUser) return handleCreate();

    setSaving(true); setError('');
    try {
      const projectIds = form.projectIds.split(',').map(p => p.trim()).filter(Boolean);
      await api.put(`/users/${editingUser.id}`, {
        name: form.name,
        email: form.email,
        role: form.role,
        projectIds,
        vendor_user_id: form.vendor_user_id,
        phone: form.phone,
        organization: form.organization,
        organization_address: form.organization_address,
        city: form.city,
        state: form.state,
        country: form.country,
        postal_code: form.postal_code,
      });
      setSuccess('User updated successfully');
      setShowModal(false);
      setEditingUser(null);
      setForm(emptyForm);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const openProjectDetails = async (selectedUser) => {
    if (!selectedUser.vendor_project_count) return;

    setProjectDetailsLoading(true);
    setProjectDetails({ user: selectedUser, projects: [] });
    try {
      const { data } = await api.get(`/users/${selectedUser.id}/vendor-projects`);
      setProjectDetails({ user: selectedUser, projects: data.projects || [] });
    } catch (err) {
      setProjectDetails(null);
      setError(err.response?.data?.error || 'Failed to load vendor project details');
    } finally {
      setProjectDetailsLoading(false);
    }
  };

  const openGpuCapacity = async (selectedUser) => {
    const ownerId = selectedUser.role === 'client_user' ? (selectedUser.client_owner_id || selectedUser.id) : selectedUser.id;
    setGpuCapacityError('');
    setGpuCapacityLoading(true);
    setGpuCapacityOwner({ id: ownerId, name: selectedUser.client_owner_name || selectedUser.name });
    setGpuCapacityRows([]);
    setGpuCapacityModalOpen(true);

    try {
      const { data } = await api.get(`/users/${ownerId}/gpu-capacity`);
      setGpuCapacityRows(data.gpuCapacity || []);
    } catch (err) {
      setGpuCapacityError(err.response?.data?.error || 'Failed to load GPU capacity');
    } finally {
      setGpuCapacityLoading(false);
    }
  };

  const saveGpuCapacityRow = async (row) => {
    if (!gpuCapacityOwner) return;
    try {
      const payload = {
        id: row.id,
        gpuModel: row.gpuModel,
        committed: Number(row.committed) || 0,
        utilised: Number(row.utilised) || 0,
        available: Number(row.available) || 0,
      };
      const { data } = await api.post(`/users/${gpuCapacityOwner.id}/gpu-capacity`, payload);
      setGpuCapacityRows((currentRows) => currentRows.map((item) => {
        const match = row.id ? item.id === row.id : item.tempId === row.tempId;
        return match ? { ...item, ...payload, id: item.id || data.id, tempId: item.tempId } : item;
      }));
    } catch (err) {
      setGpuCapacityError(err.response?.data?.error || 'Failed to save GPU capacity');
    }
  };

  const deleteGpuCapacityRow = async (row) => {
    if (!gpuCapacityOwner) return;
    if (!row.id) {
      setGpuCapacityRows((currentRows) => currentRows.filter((item) => item.tempId !== row.tempId));
      return;
    }
    try {
      await api.delete(`/users/${gpuCapacityOwner.id}/gpu-capacity/${row.id}`);
      setGpuCapacityRows((currentRows) => currentRows.filter((item) => item.id !== row.id));
    } catch (err) {
      setGpuCapacityError(err.response?.data?.error || 'Failed to remove GPU capacity row');
    }
  };

  const addGpuCapacityRow = () => {
    setGpuCapacityRows((currentRows) => ([
      ...currentRows,
      { id: null, tempId: `new-${Date.now()}-${Math.random()}`, gpuModel: '', committed: '', utilised: '', available: '' },
    ]));
  };

  return (
    <div style={styles.layout}>
      <Sidebar />
      <main style={styles.main}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.pageTitle}>{isAdmin ? 'Manage Clients' : 'Manage Users'}</h1>
            <p style={styles.pageSubtitle}>
              {isAdmin ? 'Create client accounts and view every client user' : 'Create users manually or use your client API credentials'}
            </p>
          </div>
          {canCreateUsers && (
            <button style={styles.primaryBtn} onClick={openCreate}>{isAdmin ? '+ Add Client' : '+ Add User'}</button>
          )}
        </div>

        {success && <div style={styles.successBox}>{success} <button onClick={() => setSuccess('')} style={styles.closeBtn}>✕</button></div>}
        {error && <div style={styles.errorBox}>{error} <button onClick={() => setError('')} style={styles.closeBtn}>✕</button></div>}

        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['User', isAdmin ? 'Owner' : 'Role', 'Projects', 'Status', 'Received', 'Actions'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ ...styles.td, textAlign: 'center', padding: 40 }}>Loading users...</td></tr>
              ) : users.map(u => (
                <tr key={u.id}>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ ...styles.avatar, background: u.role === 'admin' ? 'linear-gradient(135deg, #FBBC05, #e6a300)' : 'linear-gradient(135deg, #4285F4, #1a73e8)' }}>
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div style={styles.userName}>{u.name}</div>
                        <div style={styles.userEmail}>{u.email}</div>
                        {u.client_owner_name && <div style={styles.projectList}>Client: {u.client_owner_name}</div>}
                        {u.vendor_user_id && <div style={styles.projectList}>Vendor ID: {u.vendor_user_id}</div>}
                        {u.organization && <div style={styles.projectList}>{u.organization}</div>}
                        {(u.city || u.state || u.country) && <div style={styles.projectList}>{[u.city, u.state, u.country].filter(Boolean).join(', ')}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, ...(u.role === 'admin' ? styles.adminBadge : styles.clientBadge) }}>
                      {u.role === 'admin' ? '👑 Admin' : '👤 Client'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.projectList,
                        cursor: u.vendor_project_count ? 'pointer' : 'default',
                        color: u.vendor_project_count ? '#2563eb' : '#94a3b8',
                        textDecoration: u.vendor_project_count ? 'underline' : 'none',
                      }}
                      title={u.vendor_project_count ? 'View vendor project details' : undefined}
                      onClick={() => openProjectDetails(u)}
                    >
                      {u.vendor_project_titles || u.vendor_project_ids || u.assigned_projects || 'None'}
                    </span>
                    {u.source && <div style={styles.userEmail}>Source: {u.source}</div>}
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, ...(u.is_active ? styles.activeBadge : styles.inactiveBadge) }}>
                      {u.is_active ? '● Active' : '○ Inactive'}
                    </span>
                  </td>
                  <td style={styles.td}>{u.last_received_at ? new Date(u.last_received_at).toLocaleDateString() : new Date(u.created_at).toLocaleDateString()}</td>
                  <td style={styles.td}>
                    {isAdmin && (
                      <>
                        <button
                          style={{ ...styles.actionBtn, color: '#2563eb', marginRight: 10 }}
                          onClick={() => openEdit(u)}
                        >
                          Edit
                        </button>
                        {u.role !== 'admin' && (
                          <button
                            style={{ ...styles.actionBtn, color: '#0f766e', marginRight: 10 }}
                            onClick={() => openGpuCapacity(u)}
                          >
                            GPU Capacity
                          </button>
                        )}
                      </>
                    )}
                    {/* <button
                      style={{ ...styles.actionBtn, color: u.is_active ? '#f87171' : '#4ade80' }}
                      onClick={() => toggleActive(u.id, u.is_active)}
                    >
                      {u.is_active ? 'Deactivate' : 'Activate'}
                    </button> */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modal */}
        {showModal && (
          <div style={styles.overlay}>
            <div style={styles.modal}>
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>{editingUser ? 'Edit User' : (isAdmin ? 'Add New Client' : 'Add New User')}</h3>
                <button style={styles.closeBtn} onClick={() => setShowModal(false)}>✕</button>
              </div>

              {[
                { label: 'Full Name', key: 'name', type: 'text', placeholder: 'John Smith' },
                { label: 'Email', key: 'email', type: 'email', placeholder: 'john@company.com' },
                ...(!editingUser ? [{ label: 'Password (optional)', key: 'password', type: 'password', placeholder: 'Leave blank to generate' }] : []),
                { label: 'GCP Project IDs (comma-separated)', key: 'projectIds', type: 'text', placeholder: 'my-project-prod, my-project-dev' },
              ].map(f => (
                <div key={f.key} style={styles.field}>
                  <label style={styles.label}>{f.label}</label>
                  <input
                    type={f.type}
                    value={form[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    style={styles.input}
                  />
                </div>
              ))}

              {isAdmin && (
                <>
                  <div style={styles.field}>
                    <label style={styles.label}>Role</label>
                    <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} style={styles.input}>
                      <option value="client">Client</option>
                      <option value="client_user">Client User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  {[
                    { label: 'Vendor User ID', key: 'vendor_user_id', placeholder: 'Vendor user reference' },
                    { label: 'Phone', key: 'phone', placeholder: '+91...' },
                    { label: 'Organization', key: 'organization', placeholder: 'Client organization' },
                    { label: 'Organization Address', key: 'organization_address', placeholder: 'Billing or registered address' },
                    { label: 'City', key: 'city', placeholder: 'City' },
                    { label: 'State', key: 'state', placeholder: 'State' },
                    { label: 'Country', key: 'country', placeholder: 'Country' },
                    { label: 'Postal Code', key: 'postal_code', placeholder: 'Postal code' },
                  ].map(f => (
                    <div key={f.key} style={styles.field}>
                      <label style={styles.label}>{f.label}</label>
                      <input
                        type="text"
                        value={form[f.key]}
                        onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                        placeholder={f.placeholder}
                        style={styles.input}
                      />
                    </div>
                  ))}
                </>
              )}

              {error && <div style={styles.errorBox}>{error}</div>}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
                <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
                <button style={styles.primaryBtn} onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : (editingUser ? 'Save Changes' : (isAdmin ? 'Create Client' : 'Create User'))}
                </button>
              </div>
            </div>
          </div>
        )}

        {gpuCapacityModalOpen && (
          <div style={styles.overlay}>
            <div style={{ ...styles.modal, width: 'min(860px, 100%)' }}>
              <div style={styles.modalHeader}>
                <div>
                  <h3 style={styles.modalTitle}>GPU Capacity — {gpuCapacityOwner?.name || 'Client'}</h3>
                  <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>Manage GPU model allocations for this client.</p>
                </div>
                <button style={styles.closeBtn} onClick={() => setGpuCapacityModalOpen(false)}>✕</button>
              </div>

              {gpuCapacityError && <div style={styles.errorBox}>{gpuCapacityError}</div>}
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button style={styles.primaryBtn} onClick={addGpuCapacityRow}>+ Add Row</button>
                {gpuCapacityLoading && <span style={{ color: '#64748b' }}>Loading...</span>}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {['GPU Model', 'Committed by CSP', 'Utilised by users', 'Available for allocation', 'Actions'].map((header) => (
                        <th key={header} style={styles.th}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {gpuCapacityRows.length ? gpuCapacityRows.map((row, rowIndex) => (
                      <tr key={row.id || row.tempId || `new-${rowIndex}`}>
                        <td style={styles.td}>
                          <input
                            type="text"
                            value={row.gpuModel}
                            placeholder="GPU Model"
                            onChange={(e) => setGpuCapacityRows((current) => current.map((item, index) => index === rowIndex ? { ...item, gpuModel: e.target.value } : item))}
                            style={styles.input}
                          />
                        </td>
                        <td style={styles.td}>
                          <input
                            type="number"
                            value={row.committed}
                            min="0"
                            onChange={(e) => setGpuCapacityRows((current) => current.map((item, index) => index === rowIndex ? { ...item, committed: e.target.value } : item))}
                            style={styles.input}
                          />
                        </td>
                        <td style={styles.td}>
                          <input
                            type="number"
                            value={row.utilised}
                            min="0"
                            onChange={(e) => setGpuCapacityRows((current) => current.map((item, index) => index === rowIndex ? { ...item, utilised: e.target.value } : item))}
                            style={styles.input}
                          />
                        </td>
                        <td style={styles.td}>
                          <input
                            type="number"
                            value={row.available}
                            min="0"
                            onChange={(e) => setGpuCapacityRows((current) => current.map((item, index) => index === rowIndex ? { ...item, available: e.target.value } : item))}
                            style={styles.input}
                          />
                        </td>
                        <td style={styles.td}>
                          <button style={{ ...styles.actionBtn, color: '#16a34a', marginRight: 10 }} onClick={() => saveGpuCapacityRow(row)}>Save</button>
                          <button style={{ ...styles.actionBtn, color: '#ef4444' }} onClick={() => deleteGpuCapacityRow(row)}>Delete</button>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={5} style={{ ...styles.td, textAlign: 'center', color: '#94a3b8' }}>
                          No GPU capacity rows configured yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {projectDetails && (
          <VendorProjectsModal
            user={projectDetails.user}
            projects={projectDetails.projects}
            loading={projectDetailsLoading}
            onClose={() => setProjectDetails(null)}
          />
        )}
      </main>
    </div>
  );
}

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toISOString().slice(0, 10);
};

const formatValue = (value) => (value === undefined || value === null || value === '' ? '-' : value);

function VendorProjectsModal({ user, projects, loading, onClose }) {
  const [selectedProject, setSelectedProject] = useState(null);
  const project = selectedProject || projects[0];

  useEffect(() => {
    setSelectedProject(null);
  }, [projects]);

  return (
    <div style={styles.detailsOverlay} onClick={onClose}>
      <div style={styles.detailsModal} onClick={(event) => event.stopPropagation()}>
        <div style={styles.detailsHeader}>
          <h2 style={styles.detailsTitle}>Project Details</h2>
          <button style={styles.detailsClose} onClick={onClose}>x</button>
        </div>

        {loading ? (
          <div style={styles.detailsEmpty}>Loading project details...</div>
        ) : !project ? (
          <div style={styles.detailsEmpty}>No vendor projects received for {user.name}.</div>
        ) : (
          <>
            {projects.length > 1 && (
              <div style={styles.projectTabs}>
                {projects.map((item) => (
                  <button
                    key={item.id}
                    style={{
                      ...styles.projectTab,
                      ...(item.id === project.id ? styles.projectTabActive : {}),
                    }}
                    onClick={() => setSelectedProject(item)}
                  >
                    {item.title || item.vendor_project_id}
                  </button>
                ))}
              </div>
            )}
            <ProjectDetailsContent project={project} fallbackUser={user} />
          </>
        )}
      </div>
    </div>
  );
}

function ProjectDetailsContent({ project, fallbackUser }) {
  const services = project.services || [];
  const computeServices = services.filter((service) => service.service_category === 'compute');
  const storageServices = services.filter((service) => service.service_category === 'storage');
  const platformServices = services.filter((service) => service.service_category === 'platform');
  const address = [
    project.organization_address || fallbackUser.organization_address,
    project.city || fallbackUser.city,
    project.state || fallbackUser.state,
    project.country || fallbackUser.country,
    project.postal_code || fallbackUser.postal_code,
  ].filter(Boolean).join(', ');

  return (
    <>
      <div style={styles.detailsSection}>
        <DetailLine label="Title" value={project.title} />
        <DetailLine label="Description" value={project.description} multiline />
        <DetailLine label="Priority" value={project.priority} />
        <DetailLine label="Subsidy Percentage" value={project.subsidy_percentage ? `${project.subsidy_percentage}%` : '-'} />
        <DetailLine label="Subsidy Max Amount" value={project.subsidy_max_amount} />
        <DetailLine label="Source" value={project.source} />
        <DetailLine label="Start Date" value={formatDate(project.start_date)} />
        <DetailLine label="End Date" value={formatDate(project.end_date)} />
      </div>

      <h3 style={styles.detailsHeading}>User Info</h3>
      <div style={styles.detailsSection}>
        <DetailLine label="Name" value={project.user_name || fallbackUser.name} />
        <DetailLine label="Email" value={project.user_email || fallbackUser.email} />
        <DetailLine label="Phone" value={project.user_phone || fallbackUser.phone} />
        <DetailLine label="Organization" value={project.organization || fallbackUser.organization} />
        <DetailLine label="Address" value={address} multiline />
      </div>

      <ServiceTable
        title="Compute Services"
        rows={computeServices}
        columns={[
          ['Instance ID', 'service_identifier'],
          ['Quantity', 'quantity'],
          ['Type', 'subscription_type'],
          ['Max Hours', 'max_hours'],
          ['Storage', 'storage_details'],
        ]}
      />
      <ServiceTable
        title="Storage Services"
        rows={storageServices}
        columns={[
          ['Type', 'service_identifier'],
          ['Quantity (GB)', 'quantity'],
          ['Months', 'months'],
        ]}
      />
      <ServiceTable
        title="Platform Services"
        rows={platformServices}
        columns={[
          ['Type', 'service_identifier'],
          ['Discount %', 'discount_percent'],
          ['Public URL', 'public_url'],
        ]}
      />
    </>
  );
}

function DetailLine({ label, value }) {
  return (
    <div>
      <strong>{label}:</strong> <span>{formatValue(value)}</span>
    </div>
  );
}

function ServiceTable({ title, rows, columns }) {
  if (!rows.length) return null;
  return (
    <>
      <h3 style={styles.detailsHeading}>{title}</h3>
      <table style={styles.detailsTable}>
        <thead>
          <tr>{columns.map(([label]) => <th key={label} style={styles.detailsTh}>{label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {columns.map(([label, key]) => <td key={label} style={styles.detailsTd}>{formatValue(row[key])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

const styles = {
  layout: { display: 'flex', background: '#f5f7fa', minHeight: '100vh', fontFamily: "'Inter', 'Segoe UI', sans-serif" },
  main: { marginLeft:0, flex:1, padding:'12px 12px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  pageTitle: { margin: 0, color: '#0f172a', fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px' },
  pageSubtitle: { margin: '4px 0 0', color: '#64748b', fontSize: 13 },
  primaryBtn: { background: 'linear-gradient(135deg, #4285F4, #1a73e8)', border: 'none', color: '#fff', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  tableCard: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', color: '#64748b', fontSize: 11, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase', padding: '14px 18px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' },
  td: { padding: '16px 18px', color: '#94a3b8', fontSize: 14, borderBottom: '1px solid #f8fafc' },
  avatar: { width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 14, flexShrink: 0 },
  userName: { color: '#1e293b', fontWeight: 600, fontSize: 14 },
  userEmail: { color: '#64748b', fontSize: 12 },
  badge: { display: 'inline-block', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  adminBadge: { background: 'rgba(251,191,36,0.1)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.2)' },
  clientBadge: { background: 'rgba(66,133,244,0.1)', color: '#60a5fa', border: '1px solid rgba(66,133,244,0.2)' },
  activeBadge: { background: 'rgba(74,222,128,0.1)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.2)' },
  inactiveBadge: { background: 'rgba(100,116,139,0.1)', color: '#94a3b8', border: '1px solid rgba(100,116,139,0.2)' },
  projectList: { fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' },
  actionBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  successBox: { background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.2)', color: '#4ade80', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  errorBox: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#fca5a5', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 32, width: '100%', maxWidth: 480 },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  modalTitle: { margin: 0, color: '#0f172a', fontSize: 20, fontWeight: 700 },
  field: { marginBottom: 16 },
  label: { display: 'block', color: '#94a3b8', fontSize: 12, fontWeight: 600, letterSpacing: '0.3px', textTransform: 'uppercase', marginBottom: 6 },
  input: { width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', color: '#0f172a', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  cancelBtn: { background: '#f8fafc', border: '1px solid #e2e8f0', color: '#94a3b8', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontSize: 13 },
  closeBtn: { background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 16 },
  detailsOverlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 110, padding: 24 },
  detailsModal: { background: '#ffffff', width: 'min(760px, 100%)', maxHeight: '88vh', overflowY: 'auto', borderRadius: 8, padding: '18px 24px 24px', boxShadow: '0 20px 50px rgba(15,23,42,0.25)' },
  detailsHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: 8, marginBottom: 14 },
  detailsTitle: { margin: 0, color: '#0f172a', fontSize: 18, fontWeight: 700 },
  detailsClose: { border: 'none', background: 'transparent', color: '#475569', fontSize: 24, lineHeight: 1, cursor: 'pointer' },
  detailsSection: { display: 'flex', flexDirection: 'column', gap: 8, color: '#52525b', fontSize: 13, lineHeight: 1.55 },
  detailsHeading: { margin: '22px 0 10px', color: '#0f172a', fontSize: 16, fontWeight: 700 },
  detailsTable: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, border: '1px solid #dbe3ee', borderRadius: 6, overflow: 'hidden', fontSize: 12, marginBottom: 6 },
  detailsTh: { background: '#e8eef7', color: '#0f172a', textAlign: 'left', fontWeight: 600, padding: '7px 10px' },
  detailsTd: { color: '#111827', padding: '7px 10px', borderTop: '1px solid #eef2f7' },
  detailsEmpty: { color: '#64748b', fontSize: 14, padding: '28px 0', textAlign: 'center' },
  projectTabs: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  projectTab: { border: '1px solid #dbe3ee', background: '#f8fafc', color: '#475569', borderRadius: 6, padding: '7px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  projectTabActive: { background: '#eff6ff', borderColor: '#bfdbfe', color: '#1d4ed8' },
};
